<!--
  FileSearchResults.vue

  Library search results as dropdown items: the newest files with no query,
  the matching ones as you type, of one kind when `view` is set. Emits the
  picked asset.
-->
<template>
  <div class="file-search-results">
    <div class="dropdown-heading">{{ query.trim() ? "Matching files" : "Recent files" }}</div>
    <p v-if="loading && !results.length" class="dropdown-item text-muted">Searching…</p>
    <p v-else-if="!results.length" class="dropdown-item text-muted">No files found</p>
    <a
      v-for="asset in results"
      :key="asset.id"
      class="dropdown-item result"
      :title="asset.name ?? undefined"
      @click="emit('pick', asset)"
    >
      <FileThumb :asset="asset" :square="32" />
      <span class="text">
        <span class="is-elided">{{ asset.name || "Untitled" }}</span>
        <span class="text-7 text-muted">{{ meta(asset) }}</span>
      </span>
    </a>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { debounce } from "lodash-es"
import type { AssetSearchItem, AssetView } from "@auger/core"
import { formatFileSize } from "@auger/ui/utils/number"
import { client } from "@/init/apollo"
import { getAuger } from "@/init/auger"
import FileThumb from "./FileThumb.vue"

const props = withDefaults(
  defineProps<{
    query?: string
    exclude?: number[]
    /** Only files of this kind. */
    view?: AssetView
  }>(),
  { query: "", exclude: () => [], view: undefined }
)

const emit = defineEmits<{ pick: [asset: AssetSearchItem] }>()

const found = ref<AssetSearchItem[]>([])
const loading = ref(false)
const results = computed(() => found.value.filter((a) => !props.exclude.includes(a.id)))

let token = 0
function run() {
  const mine = ++token
  loading.value = true
  getAuger(client)
    .assets.search({ text: props.query, view: props.view, limit: 12 })
    .then((page) => {
      if (mine === token) found.value = page.items
    })
    .catch((err: unknown) => console.error("FileSearchResults: search failed", err))
    .finally(() => {
      if (mine === token) loading.value = false
    })
}
const runSoon = debounce(run, 200)
watch(() => props.query, runSoon)
run()

function meta(asset: AssetSearchItem) {
  const uses = asset.attachments_aggregate?.aggregate?.count ?? 0
  return [
    asset.size ? formatFileSize(asset.size, 1) : null,
    uses ? `${uses} ${uses === 1 ? "record" : "records"}` : "not attached",
  ]
    .filter(Boolean)
    .join(" · ")
}
</script>

<style>
.file-search-results {
  .result {
    display: flex;
    align-items: center;
    gap: 0.6rem;
  }

  .text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
}
</style>
